<template>
  <a-layout-header class="layout-header">
    <div class="header-content">
      <!-- Left Section: Logo + App Name + Sidebar Toggle + Breadcrumb -->
      <div class="header-left">
        <!-- Logo and App Name -->
        <div class="logo-section">
          <div class="logo">
            <img
              src="/logo.png"
              alt="ClinVant Logo"
              class="logo-img"
            >
          </div>
          <div class="app-info">
            <h1 class="app-title">
              ClinVant
            </h1>
          </div>
        </div>

        <!-- Sidebar Toggle Button -->
        <a-button
          type="text"
          class="sidebar-toggle"
          data-testid="sidebar-toggle"
          @click="toggleSidebar"
        >
          <template #icon>
            <icon-menu-fold v-if="!props.collapsed" />
            <icon-menu-unfold v-else />
          </template>
        </a-button>

        <!-- Breadcrumb Navigation -->
        <a-breadcrumb
          v-if="breadcrumbItems.length > 0"
          class="breadcrumb"
        >
          <a-breadcrumb-item
            v-for="(item, index) in breadcrumbItems"
            :key="item.name + index"
          >
            <router-link
              v-if="index < breadcrumbItems.length - 1 && item.route"
              :to="item.route"
            >
              {{ item.name }}
            </router-link>
            <span
              v-else
              class="breadcrumb-current"
            >{{ item.name }}</span>
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>

      <!-- Center Section: Study Switcher + Role/Site Selector -->
      <div class="header-center">
        <StudySwitcher />
        <RoleSiteSelector />
      </div>

      <!-- Right Section: Actions -->
      <div class="header-actions">
        <a-space>
          <!-- Notification Button -->
          <a-dropdown
            trigger="hover"
            position="br"
          >
            <a-button
              type="text"
              class="notification-button"
            >
              <template #icon>
                <icon-notification />
              </template>
              <a-badge
                v-if="unreadCount > 0"
                :count="unreadCount"
                :max-count="99"
              />
            </a-button>
            <template #content>
              <div class="notification-header">
                <span class="notification-title">通知</span>
                <a-button
                  type="text"
                  size="small"
                  @click="markAllAsRead"
                >
                  全部已读
                </a-button>
              </div>
              <a-list
                :bordered="false"
                class="notification-list"
              >
                <a-list-item
                  v-for="notification in notifications"
                  :key="notification.id"
                >
                  <div class="notification-item">
                    <div class="notification-content">
                      <div class="notification-title-text">
                        {{ notification.title }}
                      </div>
                      <div class="notification-desc">
                        {{ notification.description }}
                      </div>
                      <div class="notification-time">
                        {{ formatTime(notification.created_at) }}
                      </div>
                    </div>
                    <div
                      v-if="!notification.read"
                      class="notification-dot"
                    />
                  </div>
                </a-list-item>
                <a-empty
                  v-if="notifications.length === 0"
                  description="暂无通知"
                />
              </a-list>
              <div
                v-if="notifications.length > 0"
                class="notification-footer"
              >
                <a-button
                  type="text"
                  long
                  @click="viewAllNotifications"
                >
                  查看全部通知
                </a-button>
              </div>
            </template>
          </a-dropdown>

          <!-- Theme Toggle Button -->
          <a-button
            type="text"
            class="theme-toggle"
            data-testid="theme-toggle"
            @click="toggleTheme"
          >
            <template #icon>
              <icon-moon-fill v-if="isDark" />
              <icon-sun-fill v-else />
            </template>
          </a-button>

          <!-- User Dropdown -->
          <a-dropdown>
            <a-button
              type="text"
              class="user-button"
            >
              <a-avatar
                :size="32"
                :src="userAvatarUrl || undefined"
                :style="{ backgroundColor: '#0066FF', color: '#FFF' }"
              >
                {{ userAvatarText }}
              </a-avatar>
              <span class="user-name">{{ displayName }}</span>
              <icon-down />
            </a-button>
            <template #content>
              <a-doption @click="goToProfile">
                <template #icon>
                  <icon-user />
                </template>
                个人中心
              </a-doption>
              <a-doption @click="handleLogout">
                <template #icon>
                  <icon-export />
                </template>
                退出登录
              </a-doption>
            </template>
          </a-dropdown>
        </a-space>
      </div>
    </div>
  </a-layout-header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useLayoutStore } from '@/stores/layout'
import StudySwitcher from '@/components/layouts/StudySwitcher.vue'
import RoleSiteSelector from '@/components/layouts/RoleSiteSelector.vue'
import IconMenuFold from '@arco-design/web-vue/es/icon/icon-menu-fold'
import IconMenuUnfold from '@arco-design/web-vue/es/icon/icon-menu-unfold'
import IconNotification from '@arco-design/web-vue/es/icon/icon-notification'
import IconMoonFill from '@arco-design/web-vue/es/icon/icon-moon-fill'
import IconSunFill from '@arco-design/web-vue/es/icon/icon-sun-fill'
import IconUser from '@arco-design/web-vue/es/icon/icon-user'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconExport from '@arco-design/web-vue/es/icon/icon-export'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.locale('zh-cn')
dayjs.extend(relativeTime)

defineOptions({ name: 'AppHeader' })

interface Props {
  collapsed?: boolean
}

interface Emits {
  (e: 'update:collapsed', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
})

const emit = defineEmits<Emits>()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// Theme state
const layoutStore = useLayoutStore()
layoutStore.ensureHydrated()
const { isDarkTheme } = storeToRefs(layoutStore)
const isDark = computed(() => isDarkTheme.value)

// Notifications data
const notifications = ref([
  {
    id: 1,
    title: '系统通知',
    description: '欢迎使用 ClinVant 临床试验数据采集系统',
    created_at: dayjs().subtract(1, 'hour').toISOString(),
    read: false,
  },
  {
    id: 2,
    title: '数据更新',
    description: '您有新的组织数据需要审核',
    created_at: dayjs().subtract(3, 'hour').toISOString(),
    read: false,
  },
  {
    id: 3,
    title: '维护通知',
    description: '系统将于今晚22:00-23:00进行维护',
    created_at: dayjs().subtract(1, 'day').toISOString(),
    read: true,
  },
])

// Computed
const user = computed(() => authStore.user)
const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)

// 用户名显示（仅使用 fullName）
const displayName = computed(() => {
  if (!user.value) return '用户'
  const rawUser = user.value as unknown as Record<string, unknown>
  const valueText = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
  const fullName =
    valueText(rawUser.fullName) ||
    valueText(rawUser.full_name) ||
    valueText(rawUser.displayName) ||
    valueText(rawUser.name) ||
    [valueText(rawUser.firstName), valueText(rawUser.lastName)].filter(Boolean).join(' ').trim()

  return fullName || '用户'
})

const userAvatarUrl = computed(() => {
  if (!user.value) return null

  const avatarCandidates: unknown[] = [
    (user.value as any).avatarUrl,
    (user.value as any).avatar,
    (user.value as any).avatar_url,
    (user.value as any).profileImage,
    (user.value as any).profileImageUrl,
    (user.value as any).imageUrl,
  ]

  const candidate = avatarCandidates.find((item) => typeof item === 'string' && item.trim().length > 0)

  return typeof candidate === 'string' ? candidate : null
})

// 头像文字（取显示名称的第一个字符）
const userAvatarText = computed(() => {
  const name = displayName.value
  return name.trim() ? name.trim().charAt(0).toUpperCase() : '用'
})

// Breadcrumb configuration
type BreadcrumbRouteName = 'Dashboard' | 'Organizations' | 'Settings' | 'DictionaryVersions' | 'SystemConfig'

interface BreadcrumbItem {
  name: string
  route?: {
    name: BreadcrumbRouteName
  }
}

const breadcrumbMap: Record<string, BreadcrumbItem[]> = {
  '/dashboard': [{ name: '仪表板', route: { name: 'Dashboard' } }],
  '/organizations': [
    { name: '仪表板', route: { name: 'Dashboard' } },
    { name: '组织管理', route: { name: 'Organizations' } },
  ],
  '/dictionary-versions': [
    { name: '仪表板', route: { name: 'Dashboard' } },
    { name: '词典版本管理', route: { name: 'DictionaryVersions' } },
  ],
  '/settings': [
    { name: '仪表板', route: { name: 'Dashboard' } },
    { name: '系统设置', route: { name: 'Settings' } },
  ],
  '/admin/system-config': [
    { name: '仪表板', route: { name: 'Dashboard' } },
    { name: '系统配置管理', route: { name: 'SystemConfig' } },
  ],
}

const breadcrumbItems = computed(() => {
  const currentPath = route.path

  if (currentPath.startsWith('/dictionary-versions/')) {
    return [
      { name: '仪表板', route: { name: 'Dashboard' } },
      { name: '词典版本管理', route: { name: 'DictionaryVersions' } },
      { name: '版本详情' },
    ]
  }

  return breadcrumbMap[currentPath as keyof typeof breadcrumbMap] || []
})

// Methods
const toggleSidebar = () => {
  emit('update:collapsed', !props.collapsed)
}

const toggleTheme = () => {
  layoutStore.toggleTheme()
}

const markAllAsRead = () => {
  notifications.value.forEach(notification => {
    notification.read = true
  })
}

const viewAllNotifications = () => {
  router.push('/notifications')
}

const formatTime = (timeString: string) => {
  return dayjs(timeString).fromNow()
}

const goToProfile = () => {
  router.push('/profile')
}

const handleLogout = async () => {
  try {
    await authStore.logout()
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

</script>

<style scoped>
.layout-header {
  position: sticky;
  top: 0;
  z-index: 300;
  background: #fff;
  border-bottom: 1px solid var(--color-border);
  padding: 0;
  height: 64px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 120px;
}

.logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-img {
  width: 32px;
  height: 32px;
  background: transparent;
  object-fit: contain;
}

.app-info {
  display: flex;
  flex-direction: column;
}

.app-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-1);
  line-height: 1.2;
}

.app-subtitle {
  font-size: 12px;
  color: var(--color-text-3);
  margin-top: -2px;
}

.sidebar-toggle {
  margin-left: 8px;
}

.breadcrumb {
  flex: 1;
}

.breadcrumb-current {
  color: var(--color-text-1);
  font-weight: 500;
}

.header-center {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 16px;
}

.header-actions {
  display: flex;
  align-items: center;
}

.notification-button {
  position: relative;
}

.user-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.user-button:hover {
  background-color: var(--color-fill-2);
}

.user-name {
  font-size: 14px;
  color: var(--color-text-1);
  font-weight: 500;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
}

.notification-title {
  font-weight: 600;
  color: var(--color-text-1);
}

.notification-list {
  max-height: 300px;
  overflow-y: auto;
  min-width: 300px;
}

.notification-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 16px;
  gap: 12px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title-text {
  font-weight: 500;
  color: var(--color-text-1);
  margin-bottom: 4px;
}

.notification-desc {
  font-size: 13px;
  color: var(--color-text-2);
  margin-bottom: 4px;
  line-height: 1.4;
}

.notification-time {
  font-size: 12px;
  color: var(--color-text-3);
}

.notification-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--primary-6));
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-footer {
  padding: 8px 16px;
  border-top: 1px solid var(--color-border);
}

/* Dark theme styles */
:global([data-theme="dark"]) {
  .layout-header {
    background: var(--color-bg-2);
    border-bottom-color: var(--color-border-2);
  }

  .app-title {
    color: var(--color-text-1);
  }

  .breadcrumb-current {
    color: var(--color-text-1);
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 0 12px;
  }

  .logo-section {
    min-width: auto;
  }

  .app-subtitle {
    display: none;
  }

  .breadcrumb {
    display: none;
  }

  .header-center {
    display: none;
  }
}
</style>
